<script setup>
/**
 * OrderDetailsModal
 * Displays detailed order information for customers in a modal
 * Includes all order details, items, pricing, payment info (if paid), and print/PDF options
 * Reuses existing PDF generation utilities for download functionality
 */
import { computed } from 'vue'
import { usePdf } from '@/core/composables/usePdf.js'
import { generateOrderPdfHtml, generatePdfHeaderHtml } from '@/core/utils/emailTemplates.js'
import {
  formatOrderNumber,
  getStatusDisplayText,
  getStatusColorClass,
} from '@/core/services/orders.js'
import { formatPrice, formatDateTime } from '@core/utils/formatting.js'

const props = defineProps({
  /** Order data object */
  order: {
    type: Object,
    required: true,
  },
  /** Whether the modal is visible */
  visible: {
    type: Boolean,
    default: false,
  },
  /** Whether a payment is currently being processed */
  paymentLoading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'pay'])

// PDF generation
const { generating, downloadPdf } = usePdf()

// Helpers
function maskId(id) {
  if (!id) return '—'
  const str = String(id)
  if (str.length <= 4) return str
  return '*'.repeat(str.length - 4) + str.slice(-4)
}

// Computed properties
const formattedOrderNumber = computed(() => formatOrderNumber(props.order?.orderNumber))

const isPaid = computed(() => props.order?.status === 'paid')
const isPending = computed(() => props.order?.status === 'pending')

// Close modal
function closeModal() {
  emit('close')
}

/**
 * Handle print functionality
 * Opens the browser print dialog with order details
 */
function handlePrint() {
  // Map payment data to the PDF template format
  const paymentData = props.order.payment
    ? {
        amount: props.order.payment.amount,
        cardSuffix: props.order.payment.card?.lastFour,
        holderName: props.order.payment.card?.holderName,
        identificationNumber: props.order.payment.card?.identificationNumber,
        approvalNumber: props.order.payment.approvalNumber,
        numberOfPayments: props.order.payment.payments?.numberOfPayments,
        firstPaymentAmount: props.order.payment.payments?.firstPaymentAmount,
        restPaymentsAmount: props.order.payment.payments?.restPaymentsAmount,
        paidAt: props.order.paidAt,
      }
    : null

  // Generate the PDF HTML content
  const orderData = {
    client: props.order.client,
    items: props.order.items,
    totals: props.order.totals,
    deposit: props.order.deposit || { hasDeposit: false },
    orderNumber: props.order.orderNumber,
    pointsToAward: props.order.pointsToAward || 0,
    payment: paymentData,
    isPaid: isPaid.value,
  }

  const html = generateOrderPdfHtml(orderData)

  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = 'none'
  iframe.style.opacity = '0'
  document.body.appendChild(iframe)

  let printed = false
  function triggerPrint() {
    if (printed) return
    printed = true
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe)
    }, 1000)
  }

  iframe.contentDocument.write(html)
  iframe.contentDocument.close()

  iframe.onload = triggerPrint
  setTimeout(triggerPrint, 500)
}

/**
 * Handle PDF download
 * Uses the existing usePdf composable
 */
async function handleDownloadPdf() {
  // Map payment data to the PDF template format
  const paymentData = props.order.payment
    ? {
        amount: props.order.payment.amount,
        cardSuffix: props.order.payment.card?.lastFour,
        holderName: props.order.payment.card?.holderName,
        identificationNumber: props.order.payment.card?.identificationNumber,
        approvalNumber: props.order.payment.approvalNumber,
        numberOfPayments: props.order.payment.payments?.numberOfPayments,
        firstPaymentAmount: props.order.payment.payments?.firstPaymentAmount,
        restPaymentsAmount: props.order.payment.payments?.restPaymentsAmount,
        paidAt: props.order.paidAt,
      }
    : null

  const orderData = {
    client: props.order.client,
    items: props.order.items,
    totals: props.order.totals,
    deposit: props.order.deposit || { hasDeposit: false },
    orderNumber: props.order.orderNumber,
    pointsToAward: props.order.pointsToAward || 0,
    payment: paymentData,
    isPaid: isPaid.value,
  }

  const html = generateOrderPdfHtml(orderData)
  const filename = `order-${formattedOrderNumber.value}`

  // Header generator for multi-page PDFs
  const headerGenerator = (pageNum, totalPages, dateStr) => {
    return generatePdfHeaderHtml(pageNum, totalPages, dateStr, props.order.orderNumber)
  }

  await downloadPdf(html, filename, {}, headerGenerator)
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="closeModal">
      <div class="modal order-details-modal">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">הזמנה #{{ formattedOrderNumber }}</h2>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Status Banner -->
          <div class="status-banner" :class="getStatusColorClass(order.status)">
            <span class="status-label">סטטוס:</span>
            <span class="status-value">{{ getStatusDisplayText(order.status) }}</span>
          </div>

          <!-- Info Cards Grid -->
          <div class="details-grid">
            <!-- Client Info Card -->
            <section class="info-card">
              <h3 class="card-title">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                פרטי לקוח
              </h3>
              <div class="info-rows">
                <div class="info-row">
                  <span class="info-label">שם</span>
                  <span class="info-value">{{ order.client?.name || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">מק"ט</span>
                  <span class="info-value">{{ order.client?.makat || '—' }}</span>
                </div>
                <div v-if="order.client?.address" class="info-row">
                  <span class="info-label">כתובת</span>
                  <span class="info-value">{{ order.client.address }}</span>
                </div>
                <div v-if="order.client?.phone" class="info-row">
                  <span class="info-label">טלפון</span>
                  <span class="info-value">{{ order.client.phone }}</span>
                </div>
              </div>
            </section>

            <!-- Order Info Card -->
            <section class="info-card">
              <h3 class="card-title">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                פרטי הזמנה
              </h3>
              <div class="info-rows">
                <div class="info-row">
                  <span class="info-label">מספר הזמנה</span>
                  <span class="info-value order-number">{{ formattedOrderNumber }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">תאריך יצירה</span>
                  <span class="info-value">{{
                    formatDateTime(order.createdAt, { long: true })
                  }}</span>
                </div>
                <div v-if="order.paidAt" class="info-row">
                  <span class="info-label">תאריך תשלום</span>
                  <span class="info-value paid-date">{{
                    formatDateTime(order.paidAt, { long: true })
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">סה"כ פריטים</span>
                  <span class="info-value"
                    >{{ order.uniqueItemCount }} פריטים ({{ order.itemCount }} יח')</span
                  >
                </div>
                <div v-if="order.pointsToAward > 0" class="info-row">
                  <span class="info-label">נקודות לזיכוי</span>
                  <span class="info-value points">{{ order.pointsToAward }} נקודות</span>
                </div>
                <div v-if="order.payment?.transactionUid" class="info-row">
                  <span class="info-label">מזהה עסקה</span>
                  <span class="info-value transaction-id" dir="ltr">{{
                    order.payment.transactionUid
                  }}</span>
                </div>
              </div>
            </section>
          </div>

          <!-- Payment Details (only if paid) -->
          <section v-if="isPaid && order.payment" class="payment-section">
            <h3 class="section-title">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              פרטי תשלום
            </h3>
            <div class="payment-card">
              <div class="payment-row">
                <span class="payment-label">סכום ששולם</span>
                <span class="payment-value amount">{{ formatPrice(order.payment.amount) }} ₪</span>
              </div>
              <div v-if="order.payment.card?.lastFour" class="payment-row">
                <span class="payment-label">כרטיס אשראי</span>
                <span class="payment-value" dir="ltr">**** {{ order.payment.card.lastFour }}</span>
              </div>
              <div v-if="order.payment.approvalNumber" class="payment-row">
                <span class="payment-label">מספר אישור</span>
                <span class="payment-value" dir="ltr">{{ order.payment.approvalNumber }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">בעל הכרטיס</span>
                <span class="payment-value">{{ order.payment.card?.holderName || '—' }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">ת"ז בעל הכרטיס</span>
                <span class="payment-value" dir="ltr">{{
                  maskId(order.payment.card?.identificationNumber)
                }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">מספר תשלומים</span>
                <span class="payment-value">{{
                  order.payment.payments?.numberOfPayments || 1
                }}</span>
              </div>
              <div
                v-if="
                  (order.payment.payments?.numberOfPayments || 1) > 1 &&
                  order.payment.payments?.firstPaymentAmount
                "
                class="payment-row"
              >
                <span class="payment-label">תשלום ראשון</span>
                <span class="payment-value"
                  >{{ formatPrice(order.payment.payments.firstPaymentAmount) }} ₪</span
                >
              </div>
              <div
                v-if="
                  (order.payment.payments?.numberOfPayments || 1) > 1 &&
                  order.payment.payments?.firstPaymentAmount
                "
                class="payment-row"
              >
                <span class="payment-label">תשלומים נוספים</span>
                <span class="payment-value"
                  >{{ formatPrice(order.payment.payments.restPaymentsAmount || 0) }} ₪</span
                >
              </div>
            </div>
          </section>

          <!-- Items Table -->
          <section class="items-section">
            <h3 class="section-title">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
              </svg>
              פריטים בהזמנה
            </h3>

            <div class="items-table-wrapper">
              <table class="items-table">
                <thead>
                  <tr>
                    <th class="name-col">שם מוצר</th>
                    <th class="makat-col">מק"ט</th>
                    <th class="qty-col">כמות</th>
                    <th class="price-col">מחיר יח'</th>
                    <th class="total-col">סה"כ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in order.items"
                    :key="index"
                    :class="{ 'promo-row': item.isPromoLine, 'discount-row': item.isDiscountLine }"
                  >
                    <td class="name-col">
                      <span class="item-name">{{ item.name }}</span>
                      <span v-if="item.promo" class="promo-badge">{{ item.promo.label }}</span>
                      <span v-if="item.discount" class="discount-badge"
                        >הנחה {{ item.discount.discountPercent }}%</span
                      >
                    </td>
                    <td class="makat-col">{{ item.makat }}</td>
                    <td class="qty-col">{{ item.qty }}</td>
                    <td class="price-col">{{ formatPrice(item.unitPricePreVatPreDeposit) }} ₪</td>
                    <td class="total-col">{{ formatPrice(item.lineTotalPreVat) }} ₪</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Pricing Summary -->
          <section class="summary-section">
            <h3 class="section-title">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
              </svg>
              סיכום מחירים
            </h3>

            <div class="summary-card">
              <div class="summary-row">
                <span class="summary-label">סה"כ פריטים (לפני מע"מ)</span>
                <span class="summary-value"
                  >{{ formatPrice(order.totals?.itemsSubtotalPreVat) }} ₪</span
                >
              </div>

              <div v-if="order.deposit?.hasDeposit" class="summary-row">
                <span class="summary-label">פיקדון ({{ order.deposit.depositUnits }} יח')</span>
                <span class="summary-value">{{ formatPrice(order.deposit.depositPreVat) }} ₪</span>
              </div>

              <div v-if="order.totals?.promoSavingsPreVat > 0" class="summary-row savings">
                <span class="summary-label">חיסכון מבצעים</span>
                <span class="summary-value"
                  >-{{ formatPrice(order.totals.promoSavingsPreVat) }} ₪</span
                >
              </div>

              <div v-if="order.totals?.discountSavingsPreVat > 0" class="summary-row savings">
                <span class="summary-label">חיסכון הנחות</span>
                <span class="summary-value"
                  >-{{ formatPrice(order.totals.discountSavingsPreVat) }} ₪</span
                >
              </div>

              <div class="summary-row subtotal">
                <span class="summary-label">סה"כ לפני מע"מ</span>
                <span class="summary-value">{{ formatPrice(order.totals?.totalPreVat) }} ₪</span>
              </div>

              <div class="summary-row">
                <span class="summary-label">מע"מ (18%)</span>
                <span class="summary-value">{{ formatPrice(order.totals?.vatAmount) }} ₪</span>
              </div>

              <div class="summary-row grand-total">
                <span class="summary-label">{{ isPaid ? 'סה"כ שולם' : 'סה"כ לתשלום' }}</span>
                <span class="summary-value">{{ formatPrice(order.totals?.totalWithVat) }} ₪</span>
              </div>

              <div v-if="order.pointsToAward > 0" class="summary-row points">
                <span class="summary-label">נקודות {{ isPaid ? 'שזוכו' : 'שיזוכו' }}</span>
                <span class="summary-value">{{ order.pointsToAward }} נקודות</span>
              </div>
            </div>
          </section>
        </div>

        <!-- Modal Footer with Actions -->
        <div class="modal-footer">
          <button class="action-btn print-btn" @click="handlePrint">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            <span>הדפסה</span>
          </button>
          <button class="action-btn download-btn" :disabled="generating" @click="handleDownloadPdf">
            <svg
              v-if="generating"
              class="spin"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{{ generating ? 'מייצר PDF...' : 'הורד PDF' }}</span>
          </button>
          <button
            v-if="isPending"
            class="action-btn pay-btn"
            :disabled="paymentLoading"
            @click="emit('pay', order)"
          >
            <svg
              v-if="paymentLoading"
              class="spin"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            <span>{{ paymentLoading ? 'מעבד...' : 'עבור לתשלום' }}</span>
          </button>
          <button class="action-btn close-btn" @click="closeModal">
            <span>סגור</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
  backdrop-filter: blur(2px);
}

.modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  background: var(--gradient-header);
  color: white;
}

.modal-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Modal Body */
.modal-body {
  flex: 1;
  padding: var(--spacing-lg);
  overflow-y: auto;
  direction: rtl;
}

/* Status Banner */
.status-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  font-size: var(--font-size-sm);
}

.status-banner.status-new {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}

.status-banner.status-priority {
  background: rgba(40, 53, 72, 0.12);
  color: var(--color-primary);
}

.status-banner.status-pending {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}

.status-banner.status-paid {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}

.status-banner.status-processing {
  background: rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
}

.status-banner.status-shipped {
  background: rgba(51, 65, 85, 0.15);
  color: #334155;
}

.status-banner.status-delivered {
  background: rgba(34, 197, 94, 0.2);
  color: #059669;
}

.status-banner.status-cancelled {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.status-label {
  font-weight: var(--font-weight-medium);
}

.status-value {
  font-weight: var(--font-weight-bold);
}

/* Details Grid */
.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

/* Info Card */
.info-card {
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
}

.card-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm);
  padding-bottom: var(--spacing-xs);
  border-bottom: 1px solid var(--border-color);
}

.card-title svg {
  color: var(--color-primary);
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.info-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.info-value.order-number {
  font-family: monospace;
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
}

.info-value.paid-date {
  color: #15803d;
}

.info-value.points {
  color: #b45309;
  font-weight: var(--font-weight-semibold);
}

.info-value.transaction-id {
  font-family: monospace;
  font-size: var(--font-size-xs);
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Payment Section */
.payment-section {
  margin-bottom: var(--spacing-md);
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm);
}

.section-title svg {
  color: var(--color-primary);
}

.payment-card {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
}

.payment-row:not(:last-child) {
  border-bottom: 1px solid rgba(34, 197, 94, 0.15);
}

.payment-label {
  font-size: var(--font-size-sm);
  color: #166534;
}

.payment-value {
  font-size: var(--font-size-sm);
  color: #15803d;
  font-weight: var(--font-weight-medium);
}

.payment-value.amount {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
}

/* Items Section */
.items-section {
  margin-bottom: var(--spacing-md);
}

.items-table-wrapper {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  overflow-x: auto;
  border: 1px solid var(--border-color);
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 450px;
}

.items-table th {
  padding: var(--spacing-xs) var(--spacing-sm);
  text-align: right;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.items-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
  font-size: var(--font-size-xs);
}

.items-table tr:last-child td {
  border-bottom: none;
}

.name-col {
  min-width: 150px;
}

.item-name {
  display: block;
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.promo-badge,
.discount-badge {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 4px;
  font-size: 10px;
  border-radius: var(--border-radius-sm);
}

.promo-badge {
  background: rgba(51, 65, 85, 0.1);
  color: #334155;
}

.discount-badge {
  background: rgba(34, 197, 94, 0.1);
  color: #15803d;
}

.promo-row {
  background: rgba(51, 65, 85, 0.03);
}

.discount-row {
  background: rgba(34, 197, 94, 0.03);
}

.makat-col {
  color: var(--text-muted);
  font-family: monospace;
  font-size: 11px;
}

.qty-col,
.price-col,
.total-col {
  text-align: center;
}

.total-col {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

/* Summary Section */
.summary-section {
  margin-bottom: var(--spacing-sm);
}

.summary-card {
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
}

.summary-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.summary-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.summary-row.savings {
  color: #15803d;
}

.summary-row.savings .summary-value {
  color: #15803d;
}

.summary-row.subtotal {
  padding-top: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  border-top: 1px dashed var(--border-color);
}

.summary-row.grand-total {
  padding: var(--spacing-sm) 0;
  margin-top: var(--spacing-xs);
  border-top: 2px solid var(--border-color);
}

.summary-row.grand-total .summary-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.summary-row.grand-total .summary-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.summary-row.points {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.15) 100%);
  margin: var(--spacing-xs) calc(-1 * var(--spacing-md));
  margin-bottom: calc(-1 * var(--spacing-md));
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 0 0 var(--border-radius) var(--border-radius);
}

.summary-row.points .summary-label,
.summary-row.points .summary-value {
  color: #b45309;
  font-weight: var(--font-weight-semibold);
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.print-btn {
  background: linear-gradient(135deg, #283548 0%, #1e293b 100%);
  color: white;
}

.print-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(40, 53, 72, 0.3);
}

.download-btn {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
}

.download-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.download-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.pay-btn {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
}

.pay-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.pay-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.close-btn {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  margin-right: auto;
}

.close-btn:hover {
  background: var(--bg-tertiary);
}

/* Spin animation */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal {
    max-height: 95vh;
    margin: var(--spacing-sm);
  }

  .modal-body {
    padding: var(--spacing-md);
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-wrap: wrap;
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .action-btn {
    flex: 1;
    min-width: 100px;
  }

  .close-btn {
    margin-right: 0;
    flex-basis: 100%;
    order: 3;
    margin-top: var(--spacing-xs);
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .modal-title {
    font-size: var(--font-size-base);
  }

  .items-table th,
  .items-table td {
    padding: var(--spacing-xs);
    font-size: 10px;
  }

  .summary-card {
    padding: var(--spacing-sm);
  }
}
</style>
